import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, OAuthProvider, signInWithRedirect } from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import OpenShop from "./OpenShop";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOpenShop, setShowOpenShop] = useState(false);
  const [error, setError] = useState("");

  const createUserDocument = async (user, additionalData = {}) => {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      const { displayName, email } = user;
      const createdAt = new Date();
      
      try {
        await setDoc(userRef, {
          displayName,
          email,
          createdAt,
          isAdmin: false,
          ...additionalData
        });
      } catch (error) {
        console.error("Error creating user document", error);
      }
    }
    
    return userRef;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await createUserDocument(result.user);
      onLogin(result.user);
    } catch (error) {
      console.error("Login error:", error);
      setError("Inloggen mislukt: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      // Add custom parameters to improve popup behavior
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      await createUserDocument(result.user);
      onLogin(result.user);
    } catch (error) {
      console.error("Google sign-in error:", error);
      
      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Google login popup werd gesloten. Probeer opnieuw.");
      } else if (error.code === 'auth/popup-blocked') {
        setError("Popup werd geblokkeerd door je browser. Sta popups toe voor deze site.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError("Login geannuleerd. Probeer opnieuw.");
      } else {
        setError("Google inloggen mislukt: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      
      await signInWithRedirect(auth, provider);
    } catch (error) {
      console.error("Apple sign-in error:", error);
      setError("Apple inloggen mislukt: " + error.message);
      setLoading(false);
    }
  };

  const handleOpenShopSuccess = () => {
    setShowOpenShop(false);
    // Optionally refresh the page or show success message
  };

  if (showOpenShop) {
    return <OpenShop onSuccess={handleOpenShopSuccess} onBack={() => setShowOpenShop(false)} />;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }} className="auth-layout">
      <div className="card" style={{ maxWidth: '420px', width: '100%', margin: 0 }}>
        <div className="card-header text-center">
          <div style={{ fontSize: '2rem', marginBottom: '4px' }}>☕</div>
          <h1>Cupdate Portal</h1>
          <p>Beheer je winkel en klanten</p>
        </div>

        <div className="card-body">
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="jij@winkel.nl"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Wachtwoord</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '22px 0' }}>
            <span style={{ flex: 1, height: '1px', background: 'var(--hairline)' }} />
            <span style={{ fontSize: '0.8rem', color: 'var(--ink-soft)', fontWeight: 500 }}>of</span>
            <span style={{ flex: 1, height: '1px', background: 'var(--hairline)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              onClick={handleGoogleSignIn}
              className="btn btn-secondary w-full"
              disabled={loading}
            >
              Inloggen met Google
            </button>

            <button
              onClick={handleAppleSignIn}
              className="btn btn-secondary w-full"
              disabled={loading}
            >
              Inloggen met Apple
            </button>
          </div>

          <div style={{ marginTop: '22px', paddingTop: '20px', borderTop: '1px solid var(--hairline)', textAlign: 'center' }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--ink-soft)', marginBottom: '10px' }}>
              Nog geen winkel?
            </p>
            <button
              onClick={() => setShowOpenShop(true)}
              className="btn btn-success w-full"
            >
              Winkel aanmaken
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 